// src/pages/MeetingRequest.tsx
//
// Native replacement for the "Meeting Request" Google Form + Zapier flow (the GForm
// titled "CBAM FUND Event - 1 - Request"). Investment-team gated (route + server RLS).
// Keeps the original 5-section wizard UX + the transcribed GForm field labels/help text,
// but is now lookup-driven (people/houses + a searchable meeting_fund_lookup picker) and
// wired to the meeting-book edge fn through the same offline-safe outbox as MeetingBooked.
// A REQUEST is not a booking: calendar_mode is always 'none' (no calendar event); the
// Asana task assigns to the booker. Identity emails/GIDs are resolved server-side.

import { useEffect, useMemo, useState } from "react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useMeetingLookups, type MeetingFundOption } from "@/hooks/useMeetingLookups";
import { enqueueMeeting, postMeeting, removeMeeting, drainMeetings, MeetingPostError } from "@/lib/meetings/meetingOutbox";
import { useIsOnline } from "@/hooks/useIsOnline";
import { ChevronLeft, ChevronRight, Loader2, Upload } from "lucide-react";

const NEW_HOUSE = "__new__";

// Normalise a fund identifier for comparison (ISIN/SEDOL are case-insensitive, no spaces/dashes).
const normId = (v: string | null | undefined): string => (v ?? "").toUpperCase().replace(/[\s-]/g, "");

const MeetingRequest = () => {
  // SUBSCRIBE, do not read the module cache. useIsOnline's cache is only updated while a
  // component calling THIS HOOK is mounted (its setOnline call sites all live in the hook's
  // effect), and nothing else on this route mounts it — so getIsOnline() here would be frozen
  // at module-evaluation time. See the note in the submit catch below.
  const online = useIsOnline();
  const { toast } = useToast();
  const { user } = useAuth();
  const { fundHouses, meetingFunds, people, isLoading } = useMeetingLookups();

  const [currentSection, setCurrentSection] = useState(1);

  // Section 1
  const [bookerId, setBookerId] = useState("");          // booked_by_staff_id
  const [fundHouseId, setFundHouseId] = useState("");
  const [newHouseName, setNewHouseName] = useState("");

  // Section 3 — fund (searchable picker over meeting_fund_lookup, free-text fallback)
  const [fundName, setFundName] = useState("");          // canonical submitted value (picked or free-typed)
  const [showFundList, setShowFundList] = useState(false);
  const [selectedFundId, setSelectedFundId] = useState(""); // meeting_fund_lookup id (reference only)
  const [fundIsin, setFundIsin] = useState("");
  const [fundClass, setFundClass] = useState("");
  const [fundOcf, setFundOcf] = useState<number | null>(null);   // <- ter_pct
  const [fundAmc, setFundAmc] = useState<number | null>(null);
  const [fundAccInc, setFundAccInc] = useState<string | null>(null); // <- inc_acc (Asana passthrough)
  const [alreadyHold, setAlreadyHold] = useState("");
  const [availableOnAbrdn, setAvailableOnAbrdn] = useState("");

  // Section 4
  const [meetingType, setMeetingType] = useState("");
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);  // cbam_attendee_staff_ids
  const [fundHouseAttendees, setFundHouseAttendees] = useState(""); // requested_fund_house_attendees

  // Section 5
  const [requesterNotes, setRequesterNotes] = useState("");
  const [documents, setDocuments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isNewHouse = fundHouseId === NEW_HOUSE;

  // Filtered fund matches: substring on name OR ISIN, case-insensitive, capped at 50.
  // Empty meetingFunds (table not yet imported) => no matches, and the free-text name still works.
  const fundMatches = useMemo(() => {
    const q = fundName.trim().toLowerCase();
    if (!q) return [];
    const qn = normId(q);
    return meetingFunds
      .filter(
        (f) =>
          f.fund_name.toLowerCase().includes(q) ||
          (!!f.isin && normId(f.isin).includes(qn)),
      )
      .slice(0, 50);
  }, [meetingFunds, fundName]);

  // Drain any queued (offline) submissions on mount + whenever the tab comes online.
  useEffect(() => {
    void drainMeetings();
    const onOnline = () => void drainMeetings();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  const toggleAttendee = (id: string, checked: boolean) => {
    setAttendeeIds((prev) => (checked ? [...prev, id] : prev.filter((a) => a !== id)));
  };

  const pickFund = (f: MeetingFundOption) => {
    setSelectedFundId(f.id);
    setFundName(f.fund_name);
    setFundIsin(f.isin ?? "");
    setFundClass(f.fund_class ?? "");
    setFundOcf(f.ter_pct ?? null);
    setFundAmc(f.amc ?? null);
    setFundAccInc(f.inc_acc ?? null);
    if (typeof f.available_on_abrdn === "boolean") {
      setAvailableOnAbrdn(f.available_on_abrdn ? "yes" : "no");
    }
    setShowFundList(false);
  };

  const resetForm = () => {
    setCurrentSection(1);
    setBookerId(""); setFundHouseId(""); setNewHouseName("");
    setFundName(""); setShowFundList(false); setSelectedFundId(""); setFundIsin("");
    setFundClass(""); setFundOcf(null); setFundAmc(null); setFundAccInc(null);
    setAlreadyHold(""); setAvailableOnAbrdn("");
    setMeetingType(""); setAttendeeIds([]); setFundHouseAttendees("");
    setRequesterNotes(""); setDocuments([]);
  };

  const buildBooking = () => {
    const house = fundHouses.find((h) => h.id === fundHouseId);
    return {
      form_kind: "request",
      // Booker only — a request has no research owner (that's assigned when it's booked).
      booked_by_staff_id: bookerId || null,
      cbam_attendee_staff_ids: attendeeIds,
      requested_fund_house_attendees: fundHouseAttendees || null,
      // Fund: ISIN is canonical; fund_id is NOT set (no FK to meeting_fund_lookup).
      fund_name: fundName || null,
      fund_class: fundClass || null,
      isin: fundIsin || null,
      ocf_pct: fundOcf,
      amc: fundAmc,
      acc_inc: fundAccInc, // passthrough for Asana (meeting-book reads booking.acc_inc; not a column)
      fund_house_id: isNewHouse ? null : (fundHouseId || null),
      fund_house_name: isNewHouse ? newHouseName : (house?.name ?? null),
      is_new_house: isNewHouse,
      new_house_name: isNewHouse ? newHouseName : null,
      already_hold: alreadyHold === "yes" ? true : alreadyHold === "no" ? false : null,
      available_on_abrdn: availableOnAbrdn === "yes" ? true : availableOnAbrdn === "no" ? false : null,
      meeting_type: meetingType || null,
      requester_notes: requesterNotes || null,
    };
  };

  const handleSubmit = async () => {
    // Minimal required-field validation (mirrors the GForm's required questions).
    if (!bookerId) {
      toast({ title: "\"To be booked by\" is required", variant: "destructive" }); return;
    }
    if (!fundHouseId || (isNewHouse && !newHouseName)) {
      toast({ title: "Investment Manager House is required", variant: "destructive" }); return;
    }
    if (!fundName) {
      toast({ title: "Fund name is required", variant: "destructive" }); return;
    }

    const key = crypto.randomUUID();
    const booking = buildBooking();
    const attachment_keys: string[] = [];
    setSubmitting(true);
    try {
      // Upload documents to the private research-attachments bucket (best-effort, requires
      // online). Path is uid-prefixed to satisfy the bucket's write RLS.
      if (documents.length > 0 && user) {
        for (const file of documents) {
          const path = `${user.id}/meetings/${key}/${file.name}`;
          const { error } = await supabase.storage
            .from("research-attachments")
            .upload(path, file, { upsert: false });
          if (!error) attachment_keys.push(path);
        }
      }

      // A REQUEST is not a booking — never create a calendar event (calendar_mode: 'none').
      const payload = { booking, calendar_mode: "none", attachment_keys };
      // Stage first (survives a mid-request failure / offline), then attempt the post.
      enqueueMeeting(key, payload);
      const data = await postMeeting(key, payload);
      removeMeeting(key);

      toast({
        title: "Meeting request submitted",
        description: data?.asana_task_url
          ? "Asana task created and assigned to the booker."
          : "Submitted; it will be actioned in Asana.",
      });
      if (data?.asana_task_url) window.open(data.asana_task_url, "_blank", "noopener");
      resetForm();
    } catch (e) {
      // WHAT THIS CATCH MAY AND MAY NOT CLAIM.
      //
      // It used to render "Saved offline" for EVERY failure without ever reading `e`, so a
      // server refusal told the analyst their regulated request was safely queued when it had
      // just been rejected. A first fix then guarded on getIsOnline() — copied from
      // src/components/capture/CaptureSheet.tsx:330 — but copied the GUARD WITHOUT THE
      // SUBSCRIPTION, and that is the trap worth recording:
      //
      //   useIsOnline's `onlineCache` (src/hooks/useIsOnline.ts:16) is a module-level value whose
      //   ONLY writer is setOnline(), and every call site of setOnline (:74,:76,:88,:90,:91) sits
      //   inside useIsOnline()'s useEffect. The cache therefore only tracks connectivity while a
      //   component calling the HOOK is mounted. CaptureSheet does exactly that at :130. These
      //   three pages did not, and nothing in their route (partnerOpsRoutes -> ProtectedRoute ->
      //   Layout -> PartnerNavbar) mounts it either — the only global mount is OfflineBanner in
      //   src/components/mobile/MobileShell.tsx:237. So getIsOnline() here was frozen at whatever
      //   navigator.onLine said at module-evaluation time, i.e. effectively always true, and the
      //   offline branch was dead code.
      //
      // So this page now SUBSCRIBES (`const online = useIsOnline()` in the component) and
      // classifies on the typed error the outbox throws.
      const status = e instanceof MeetingPostError ? e.status : null;
      const message = e instanceof Error ? e.message : "Submission failed.";

      // No HTTP status reached us at all (transport died), or we know we are offline: this is the
      // one case that has genuinely been queued for later.
      if (status === null || !online) {
        toast({
          title: "Saved offline",
          description:
            "Your request is queued and will submit automatically when you're back online.",
        });
      } else {
        // The server answered and refused. Say so, and name the status — the page CAN now, because
        // meetingOutbox throws MeetingPostError carrying `.status` (meetingOutbox.ts:152-163,
        // thrown at :198). Earlier comments here claimed the status was unavailable; that was
        // true of an older postMeeting and is no longer.
        //
        // ⚠️ THE ITEM IS DELIBERATELY LEFT IN THE OUTBOX. An earlier fix deleted it, which
        // contradicted the outbox's own stated policy (meetingOutbox.ts:213-214: an item "KEEPS
        // its payload and its place in the outbox, never deleted, because the payload is work
        // somebody typed and this is the only copy of it") and threw away RETRYABLE failures
        // (5xx/408/429) before drainMeetings could apply the bounded retry it now implements.
        // A permanent 4xx is parked by the drain on its first pass (:268,:286) and never
        // re-posted, so leaving it queued cannot produce a duplicate booking.
        toast({
          variant: "destructive",
          title: "Request not submitted",
          description:
            `${message} (HTTP ${status}). Your answers are still on this form; nothing has been ` +
            `filed. It stays in the outbox and will not be re-sent automatically if the server ` +
            `refused it permanently.`,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const nextSection = () => {
    if (currentSection < 5) {
      // Skip section 2 (new-house input) unless "Other (new)" was chosen.
      if (currentSection === 1 && !isNewHouse) {
        setCurrentSection(3);
      } else {
        setCurrentSection((prev) => prev + 1);
      }
    }
  };

  const prevSection = () => {
    if (currentSection > 1) {
      // Skip section 2 on the way back too, when not adding a new house.
      if (currentSection === 3 && !isNewHouse) {
        setCurrentSection(1);
      } else {
        setCurrentSection((prev) => prev - 1);
      }
    }
  };

  const renderSection = () => {
    switch (currentSection) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="bookedBy" className="text-sm font-medium">To be booked by: *</Label>
              <Select value={bookerId} onValueChange={setBookerId}>
                <SelectTrigger>
                  <SelectValue placeholder={isLoading ? "Loading…" : "Select who will book the meeting"} />
                </SelectTrigger>
                <SelectContent>
                  {people.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="investmentManagerHouse" className="text-sm font-medium">
                Investment Manager House: *
                <br />
                <span className="text-xs text-muted-foreground">Fill out as described in fund Literature</span>
              </Label>
              <Select value={fundHouseId} onValueChange={setFundHouseId}>
                <SelectTrigger>
                  <SelectValue placeholder={isLoading ? "Loading…" : "Select Investment Manager House"} />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {fundHouses.map((h) => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                  <SelectItem value={NEW_HOUSE}>Other (New, Not on List)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="newFundHouseName" className="text-sm font-medium">Fund House Name</Label>
              <p className="text-xs text-muted-foreground mb-2">Name of New Fund House to Add</p>
              <Input
                id="newFundHouseName"
                value={newHouseName}
                onChange={(e) => setNewHouseName(e.target.value)}
                placeholder="Enter new fund house name"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="fundName" className="text-sm font-medium">
                Fund Name: *
                <br />
                <span className="text-xs text-muted-foreground">EXCLUDING Investment Manager House Name as completed above and AS ON FINANCIAL EXPRESS</span>
              </Label>
              <div className="relative">
                <Input
                  id="fundName"
                  value={fundName}
                  onChange={(e) => { setFundName(e.target.value); setSelectedFundId(""); setShowFundList(true); }}
                  onFocus={() => setShowFundList(true)}
                  placeholder="Search funds by name or ISIN, or type to add manually"
                  autoComplete="off"
                  required
                />
                {showFundList && fundName.trim() && fundMatches.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border bg-popover shadow-md">
                    {fundMatches.map((f) => (
                      <button
                        type="button"
                        key={f.id}
                        onClick={() => pickFund(f)}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                      >
                        <span className="font-medium">{f.fund_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {f.fund_house ? ` · ${f.fund_house}` : ""}
                          {f.fund_class ? ` · ${f.fund_class}` : ""}
                          {f.isin ? ` · ${f.isin}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Pick from the list to autofill class / OCF / ISIN, or just type the fund name if it isn't listed.
              </p>
              {fundIsin && (
                <p className="text-xs text-muted-foreground mt-1">ISIN {fundIsin}</p>
              )}
            </div>

            <div>
              <Label htmlFor="fundClass" className="text-sm font-medium">
                Fund Class
                <br />
                <span className="text-xs text-muted-foreground">Either currently holding or most likely to</span>
              </Label>
              <Input
                id="fundClass"
                value={fundClass}
                onChange={(e) => setFundClass(e.target.value)}
                placeholder="Enter fund class"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Already Hold? *</Label>
              <RadioGroup value={alreadyHold} onValueChange={setAlreadyHold} className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="hold-yes" />
                  <Label htmlFor="hold-yes">1. Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="hold-no" />
                  <Label htmlFor="hold-no">2. No</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-sm font-medium">Available on Abrdn already? *</Label>
              <RadioGroup value={availableOnAbrdn} onValueChange={setAvailableOnAbrdn} className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="abrdn-yes" />
                  <Label htmlFor="abrdn-yes">1. Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="abrdn-no" />
                  <Label htmlFor="abrdn-no">2. No</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium">Associated Meeting Type: *</Label>
              <RadioGroup value={meetingType} onValueChange={setMeetingType} className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="f2f_conference" id="f2f-conf" />
                  <Label htmlFor="f2f-conf">1. Face-to-Face (Conference, includes non CBAM attendees)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="f2f_one" id="f2f-one" />
                  <Label htmlFor="f2f-one">2. Face-to-Face (One-on-One, just CBAM)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="web_conference" id="web-conf" />
                  <Label htmlFor="web-conf">3. Web (Conference, includes non CBAM attendees)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="web_one" id="web-one" />
                  <Label htmlFor="web-one">4. Web (One-on-One, just CBAM)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no_meeting" id="no-meet" />
                  <Label htmlFor="no-meet">5. No Meeting</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-sm font-medium">CBAM Attendees: *</Label>
              <div className="mt-2 space-y-2">
                {people.map((p) => (
                  <div key={p.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`cbam-${p.id}`}
                      checked={attendeeIds.includes(p.id)}
                      onCheckedChange={(checked) => toggleAttendee(p.id, checked as boolean)}
                    />
                    <Label htmlFor={`cbam-${p.id}`}>{p.display_name}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Requested Fund House Attendees: *</Label>
              <RadioGroup value={fundHouseAttendees} onValueChange={setFundHouseAttendees} className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="bdm" id="bdm" />
                  <Label htmlFor="bdm">BDM</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="product_specialist" id="prod-spec" />
                  <Label htmlFor="prod-spec">Product Specialist only Required (No FM)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="fund_manager" id="fund-mgr" />
                  <Label htmlFor="fund-mgr">Fund Manager</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="requesterNotes" className="text-sm font-medium">Requester Soft Notes</Label>
              <Textarea
                id="requesterNotes"
                value={requesterNotes}
                onChange={(e) => setRequesterNotes(e.target.value)}
                placeholder="Add any additional notes or requirements..."
                rows={4}
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Upload any relevant Documents</Label>
              <label
                htmlFor="request-docs"
                className="mt-2 flex cursor-pointer flex-col items-center rounded-lg border-2 border-dashed border-gray-300 p-6 text-center transition-colors hover:border-gray-400"
              >
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  <span className="font-medium">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                <input
                  id="request-docs"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                  onChange={(e) => setDocuments(Array.from(e.target.files ?? []))}
                  className="hidden"
                />
              </label>
              {documents.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{documents.length} file(s) selected</p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getSectionTitle = () => {
    switch (currentSection) {
      case 1: return "Booking Request - F2F or Sole attendee Fund Meeting";
      case 2: return "New Fund House Input";
      case 3: return "Other Fund Attributes";
      case 4: return "Requested Meeting Attributes";
      case 5: return "Notes & Supporting Documents";
      default: return "";
    }
  };

  const getSectionDescription = () => {
    switch (currentSection) {
      case 1: return "Please fill in this form and then action in Asana for any other follow ups.";
      case 2:
      case 3:
      case 4:
      case 5: return "Description (optional)";
      default: return "";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Request a Fund Manager Meeting — CBAM"
        description="Submit a meeting request to the Cape Berkshire Asset Management investment team to discuss a fund, sector or portfolio opportunity."
        path="/meeting-request"
        noindex
      />
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold">
                  Section {currentSection} of 5
                </CardTitle>
                <CardDescription className="text-lg mt-1">
                  {getSectionTitle()}
                </CardDescription>
              </div>
              <div className="text-sm text-muted-foreground">
                Step {currentSection}/5
              </div>
            </div>
            {getSectionDescription() && (
              <p className="text-sm text-muted-foreground mt-2">
                {getSectionDescription()}
              </p>
            )}
          </CardHeader>

          <CardContent>
            <div className="space-y-6">
              {renderSection()}

              <div className="flex justify-between pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevSection}
                  disabled={currentSection === 1 || submitting}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                {currentSection < 5 ? (
                  <Button
                    type="button"
                    onClick={nextSection}
                    className="flex items-center gap-2"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {submitting ? "Submitting…" : "Submit Request"}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MeetingRequest;
